import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Zoom
} from "@mui/material";
import { sharedNeutrals } from "@themes/tokens/shared";
import { ReactElement, ReactNode } from "react";

export interface BaseTooltipProps extends TooltipProps {
  title: string | ReactNode;
  children: ReactElement<any, any>;
  width?: number | string;
}

const StyledTooltip = styled(
  ({ className, ...props }: TooltipProps & { className?: string }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
  { shouldForwardProp: (prop) => prop !== "width" }
)<BaseTooltipProps>(({ theme, width }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    "&:before": {
      backgroundColor: sharedNeutrals.black,
      boxShadow: theme.vars.appEffects.shadow.lg
    },
    color: sharedNeutrals.white
  },
  [`& .${tooltipClasses.tooltip}`]: {
    "& span": {
      color: sharedNeutrals.white,
      display: "block",
      fontWeight: theme.typography.fontWeightSemiBold
    },
    backgroundColor: sharedNeutrals.black,
    // border: `1px solid ${theme.vars.palette.surface.elevated}`,
    borderRadius: "8px",
    boxShadow: theme.vars.appEffects.shadow.lg,
    color: sharedNeutrals.white,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightSemiBold,
    lineHeight: "18px",
    minWidth: width
      ? typeof width === "number"
        ? `${width}px`
        : width
      : "auto",
    padding: "12px",
    whiteSpace: "pre-wrap"
  }
}));

const BaseTooltip = ({
  children,
  // A tooltip describes its trigger, it does not name it. Without this MUI
  // copies `title` onto the child as aria-label — and because most triggers are
  // a plain Box/Stack with no role, ARIA prohibits naming them, so screen
  // readers dropped the text entirely. describeChild switches to
  // aria-describedby, which is valid anywhere and is the correct relationship.
  // Triggers that carry no text of their own must still name themselves; see
  // the info-icon in BaseTypography.
  describeChild = true,
  placement = "top",
  title,
  width,
  ...props
}: BaseTooltipProps) => {
  return (
    <StyledTooltip
      slots={{ transition: Zoom }}
      describeChild={describeChild}
      title={title}
      placement={placement}
      width={width}
      arrow
      {...props}
    >
      {children}
    </StyledTooltip>
  );
};

export default BaseTooltip;
